import { toRosterMemberKey } from '@sorrem-client/ui/shared/roster-member-key';
import { useCrucibleParty } from './use-crucible-party';
import { CutscenePlayer } from '@sorrem-client/ui/features/Cutscene';
import { LoadingPage, useIsLoadingTipRead } from '@sorrem-client/ui/features/LoadingPage';
import { RunView } from '@sorrem-client/ui/features/PlayPage';
import { useAppNavigate, useSecondsUntil, useSelectedCharacterId } from '@sorrem-client/ui/hooks';
import { isPartyCrucible, MAX_PARTY_SIZE, type CrucibleMember } from '@sorrem/shared';
import { useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { EntryFlash } from './components/EntryFlash';
import { GatheringPartyStatus } from './components/GatheringPartyStatus';
import { GatheringScreen } from './components/GatheringScreen';
import { PartyChatProvider } from './components/PartyChatProvider';
import { resolveCruciblePagePhase } from './crucible-page-phase';
import { useBeforeCutscene } from './use-before-cutscene';
import { useCrucibleRoster } from './use-crucible-roster';
import { useCrucibleScene } from './use-crucible-scene';
import { useDirectEntry } from './use-direct-entry';
import { useEntryFlash } from './use-entry-flash';
import { useIsStartStalled } from './use-start-stall';
import { usePartyMembershipActions } from './use-party-membership-actions';
import { useReadyToggle } from './use-ready-toggle';
import { useRunHandoff } from './use-run-handoff';
import { useRunModelPreload } from './use-run-model-preload';

/** Stable empty-array reference - `partyState?.members ?? []` would otherwise
 * create a brand-new array every render whenever `partyState` is null,
 * which feeds into `useCrucibleRoster`'s effect dependency and causes
 * an infinite render loop (caught via a real-browser check, not typecheck). */
const NO_MEMBERS: readonly CrucibleMember[] = [];

/**
 * A direct-entry run's share of the loading bar spent on this page,
 * preloading the run's models before the host is up; the run's own loading
 * fills the rest, so the bar carries on across the handoff.
 */
const DIRECT_ENTRY_PRELOAD_SHARE = 0.4;

export function CrucibleJoinPage(): JSX.Element {
    const { instanceId } = useParams<{ instanceId: string }>();
    const crucibleId = instanceId ?? null;
    const navigate = useAppNavigate();
    const {
        myAccountId,
        partyState,
        isConnected,
        readyPayload,
        crucibleStartedAtMs,
        startCountdownEndsAtMs,
        partyIssue,
        startNotice,
        startCrucible,
        leaveCrucibleParty,
        removeCrucibleParty,
        removeMember,
        setReady,
        stageStartingCrystals,
    } = useCrucibleParty(crucibleId);
    const crucible = partyState?.crucible ?? null;
    const members = partyState?.members ?? NO_MEMBERS;
    const isCreator = myAccountId !== null && crucible !== null && myAccountId === crucible.createdBy;
    const beforeCutscene = useBeforeCutscene(crucibleId, crucible);
    const { isDirectEntry, isLoading: isDirectEntryLoading, retry: retryDirectEntry } = useDirectEntry(crucibleId, {
        crucible,
        isCreator,
        canStart: beforeCutscene.isSettled,
        startCrucible,
    });

    /* Track whether we have ever received a successful party-state update so we
       can distinguish "still connecting" from "connection lost after connecting". */
    const hasEverConnectedRef = useRef(false);
    useEffect(() => {
        if (isConnected) {
            hasEverConnectedRef.current = true;
        }
    }, [isConnected]);

    useEffect(() => {
        if (!instanceId) {
            navigate('/character/crucibles', { replace: true });
        }
    }, [instanceId, navigate]);

    const isStarting = crucibleStartedAtMs !== null && readyPayload === null;
    const countdownSecondsRemaining = useSecondsUntil(startCountdownEndsAtMs);
    // The countdown over the entrance holds at zero, its flare lit, while the run starts:
    // the server clears the countdown once the party has started (#266).
    const entryCountdownSeconds = isStarting ? 0 : countdownSecondsRemaining;
    const { selectedCharacterId: selfCharacterId } = useSelectedCharacterId();
    const self = members.find((member) => member.accountId === myAccountId && member.characterId === selfCharacterId) ?? null;
    // Ready locks gear and crystals, so the loadout the run charges is the one confirmed.
    const isLoadoutLocked = self?.isReady === true || isStarting;

    const { roster, joinedMembers, leftMembers } = useCrucibleRoster(members, crucible?.maxPlayers ?? MAX_PARTY_SIZE);
    // The scene waits until it's known whether anybody gathers: a direct-entry run is built without the gathering.
    const { session, sceneError, isGatheringBuilt, entrance } = useCrucibleScene(
        isDirectEntryLoading ? null : crucibleId,
        { hasGathering: !isDirectEntry, crucible, roster, joinedMembers, leftMembers, countdownSecondsRemaining: entryCountdownSeconds },
    );
    const { isRunLoaded, runLoadProgress } = useRunModelPreload(session, crucible?.id ?? null, roster);
    // The loading view in front of the gathering stays up long enough to read a tip, however fast the scene builds.
    const isTipRead = useIsLoadingTipRead();
    const isRunStarted = useRunHandoff({ instanceId: crucibleId, readyPayload, crucible, session });
    // The party enters through a white flash at zero, which fades off once the run shows (#266).
    // A start the host never answered stops holding the flash, so the page can say so (#328).
    const isStartStalled = useIsStartStalled(isStarting);
    const entryFlash = useEntryFlash(!isDirectEntry && entryCountdownSeconds === 0 && !isStartStalled, isRunStarted && session !== undefined);

    // Rows are keyed by account and character (resolveGatheringPartyRows).
    const selfRowKey = self === null ? null : toRosterMemberKey(self);
    const partyRowActions = usePartyMembershipActions({
        instanceId: crucibleId,
        isCreator,
        isStarting,
        members,
        selfRowKey,
        leaveCrucibleParty,
        removeCrucibleParty,
        removeMember,
    });
    const { readyError, markerToggle: partyMarkerToggle } = useReadyToggle({ self, selfRowKey, isRunLoaded, isStarting, setReady });

    const leaderAccountId = crucible?.createdBy ?? null;
    // The party's chat (the Chat tab beside Party) reads itself through PartyChatProvider; none for a crucible only one can enter.
    const isParty = crucible !== null && isPartyCrucible(crucible);
    // The viewer can ready up and hasn't: their Ready toggle glows, and the Party tab flashes while Chat shows.
    const isAwaitingReady = self !== null && self.isReady !== true && isRunLoaded && !isStarting && !isDirectEntry;

    const phase = resolveCruciblePagePhase({ isRunStarted, session, isDirectEntryLoading, isDirectEntry });
    if (phase.kind === 'run') {
        return (
            <>
                <EntryFlash phase={entryFlash} />
                <PartyChatProvider crucibleId={crucibleId} isParty={isParty}>
                    <RunView session={phase.session} loadingProgressStart={isDirectEntry ? DIRECT_ENTRY_PRELOAD_SHARE : 0} partyLeaderAccountId={leaderAccountId} />
                </PartyChatProvider>
            </>
        );
    }

    const partyStatus = (
        <GatheringPartyStatus
            isConnected={isConnected}
            hasEverConnected={hasEverConnectedRef.current}
            hasCrucible={crucible !== null}
            sceneError={sceneError}
            startNotice={startNotice}
            partyIssue={partyIssue}
            isStartStalled={isStartStalled}
            onRetryDirectEntry={isDirectEntry ? retryDirectEntry : null}
        />
    );
    const beforeCutscenePlayer = beforeCutscene.beforeCutsceneId !== null && (
        <CutscenePlayer cutsceneId={beforeCutscene.beforeCutsceneId} onFinished={beforeCutscene.finish} />
    );

    // Nobody gathers for a direct-entry run: its before-cutscene, then loading until the run takes over.
    if (phase.kind === 'deciding-entry' || phase.kind === 'direct-entry') {
        return (
            <>
                <LoadingPage progress={phase.kind === 'deciding-entry' ? 0 : DIRECT_ENTRY_PRELOAD_SHARE * runLoadProgress}>
                    {partyStatus}
                </LoadingPage>
                {beforeCutscenePlayer}
            </>
        );
    }

    return (
        <PartyChatProvider crucibleId={crucibleId} isParty={isParty}>
            <EntryFlash phase={entryFlash} />
            <GatheringScreen
                crucible={crucible}
                members={members}
                self={self}
                isGatheringReady={isGatheringBuilt && isTipRead}
                // Steps until the gathering shows: the scene started, the party state arrived, the gathering built.
                gatheringLoadProgress={((session !== undefined ? 1 : 0) + (crucible !== null ? 1 : 0)) / 3}
                partyStatus={partyStatus}
                beforeCutscene={beforeCutscenePlayer}
                isLoadoutLocked={isLoadoutLocked}
                stageStartingCrystals={stageStartingCrystals}
                isStarting={isStarting}
                isDirectEntry={isDirectEntry}
                countdownSecondsRemaining={countdownSecondsRemaining}
                entryCountdownSeconds={entryCountdownSeconds}
                entrance={entrance}
                isRunLoaded={isRunLoaded}
                readyError={readyError}
                partyRowActions={partyRowActions}
                partyMarkerToggle={partyMarkerToggle}
                isAwaitingReady={isAwaitingReady}
            />
        </PartyChatProvider>
    );
}
